/**
 * Enhanced Supabase Database Client
 * Handles all database operations for certificates and institutions
 */

import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const SUPABASE_SERVICE_ROLE_KEY = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY || '';

export const isSupabaseConfigured = () => Boolean(SUPABASE_URL && SUPABASE_ANON_KEY);

export const supabase = isSupabaseConfigured()
  ? createClient(SUPABASE_URL, SUPABASE_ANON_KEY)
  : null;

// Admin client with service role key for bypassing RLS
export const supabaseAdmin = (SUPABASE_URL && SUPABASE_SERVICE_ROLE_KEY)
  ? createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      }
    })
  : null;

export class SupabaseClient {
  
  constructor() {
    this.client = supabase;
    this.adminClient = supabaseAdmin;
    if (!isSupabaseConfigured()) {
      console.warn('⚠️ Supabase not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
    }
    if (!this.adminClient) {
      console.warn('⚠️ Admin operations may fail due to RLS. Set REACT_APP_SUPABASE_SERVICE_ROLE_KEY for full admin access.');
    }
  }

  // =================== INSTITUTIONS ===================
  
  /**
   * Get all institutions
   */
  async getInstitutions() {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('institutions')
        .select('*')
        .order('name');
      
      if (error) throw error;
      
      console.log('✅ Retrieved institutions:', data?.length || 0);
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching institutions:', error);
      return [];
    }
  }

  /**
   * Add new institution if it doesn't exist
   */
  async ensureInstitution(id, name) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const clientToUse = this.adminClient || this.client;
      
      // First check if institution exists
      const { data: existing, error: checkError } = await clientToUse
        .from('institutions')
        .select('id')
        .eq('id', id)
        .single();
      
      if (existing) {
        console.log('📍 Institution already exists:', id);
        return existing;
      }
      
      // Create new institution
      const { data, error } = await clientToUse
        .from('institutions')
        .insert([{ id, name }])
        .select()
        .single();
      
      if (error) {
        if (error.message.includes('row-level security') && !this.adminClient) {
          console.warn('⚠️ RLS prevented institution creation. Using existing institutions only.');
          return null;
        }
        throw error;
      }
      
      console.log('✅ Institution created:', data);
      return data;
    } catch (error) {
      console.error('❌ Error ensuring institution:', error);
      if (!error.message.includes('row-level security')) {
        throw error;
      }
      return null;
    }
  }

  /**
   * Add new institution manually
   */
  async addInstitution(institutionData) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      // Use admin client if available to bypass RLS, otherwise use regular client
      const clientToUse = this.adminClient || this.client;
      
      const { data, error } = await clientToUse
        .from('institutions')
        .insert([{
          id: institutionData.id || institutionData.name.toLowerCase().replace(/[^a-z0-9]/g, '_'),
          name: institutionData.name,
          website_link: institutionData.website || institutionData.website_link
        }])
        .select()
        .single();
      
      if (error) {
        // If RLS error and no admin client, provide helpful message
        if (error.message.includes('row-level security') && !this.adminClient) {
          throw new Error('Institution creation failed due to database security policies. Please contact your administrator to configure proper access permissions.');
        }
        throw error;
      }
      
      console.log('✅ Institution added:', data);
      return data;
    } catch (error) {
      console.error('❌ Error adding institution:', error);
      throw error;
    }
  }

  /**
   * Update institution
   */
  async updateInstitution(id, updates) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      // Only allow updating fields that exist in the schema
      const allowedFields = ['name', 'website_link'];
      const filteredUpdates = {};
      
      allowedFields.forEach(field => {
        if (updates[field] !== undefined) {
          filteredUpdates[field] = updates[field];
        }
      });

      const { data, error } = await this.client
        .from('institutions')
        .update(filteredUpdates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Institution updated:', data);
      return data;
    } catch (error) {
      console.error('❌ Error updating institution:', error);
      throw error;
    }
  }

  /**
   * Delete institution
   */
  async deleteInstitution(id) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const clientToUse = this.adminClient || this.client;
      
      const { error } = await clientToUse
        .from('institutions')
        .delete()
        .eq('id', id);
      
      if (error) {
        if (error.message.includes('row-level security') && !this.adminClient) {
          throw new Error('Institution deletion failed due to database security policies. Please contact your administrator.');
        }
        throw error;
      }
      
      console.log('✅ Institution deleted:', id);
      return true;
    } catch (error) {
      console.error('❌ Error deleting institution:', error);
      throw error;
    }
  }

  // =================== STUDENTS ===================

  /**
   * Add multiple students to an institution
   */
  async addStudentsBulk(institutionId, students) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const studentsData = students.map(student => ({
        institution_id: institutionId,
        first_name: student.firstName,
        last_name: student.lastName,
        roll_number: student.rollNumber,
        program: student.program,
        batch: student.batch,
        email: student.email,
        phone: student.phone
      }));

      const { data, error } = await this.client
        .from('students')
        .insert(studentsData)
        .select();
      
      if (error) throw error;
      
      console.log(`✅ Added ${data?.length || 0} students to institution:`, institutionId);
      return data || [];
    } catch (error) {
      console.error('❌ Error adding students bulk:', error);
      throw error;
    }
  }

  /**
   * Get students by institution
   */
  async getStudentsByInstitution(institutionId) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('students')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .eq('institution_id', institutionId)
        .order('roll_number');
      
      if (error) throw error;
      
      console.log(`✅ Retrieved ${data?.length || 0} students for institution:`, institutionId);
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching students by institution:', error);
      return [];
    }
  }

  /**
   * Get all students
   */
  async getAllStudents() {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('students')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .order('institution_id')
        .order('roll_number');
      
      if (error) throw error;
      
      console.log(`✅ Retrieved ${data?.length || 0} total students`);
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching all students:', error);
      return [];
    }
  }

  // =================== CERTIFICATES ===================

  /**
   * Save certificate to database with full OCR data
   */
  async saveCertificate(certificateData) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      console.log('💾 Saving certificate to database:', certificateData.file_name);

      // Ensure institution exists if institution_id is provided
      if (certificateData.institution_id && certificateData.institution) {
        await this.ensureInstitution(
          certificateData.institution_id, 
          certificateData.institution
        );
      }

      const { data, error } = await this.client
        .from('certificates')
        .insert([{
          institution: certificateData.institution,
          institution_id: certificateData.institution_id,
          student_id: certificateData.student_id,
          student_name: certificateData.student_name,
          roll_number: certificateData.roll_number,
          certificate_type: certificateData.certificate_type,
          file_name: certificateData.file_name,
          file_hash: certificateData.file_hash,
          cid: certificateData.cid,
          ipfs_url: certificateData.ipfs_url,
          anchor_tx: certificateData.anchor_tx,
          anchor_url: certificateData.anchor_url,
          ocr_data: certificateData.ocr_data,
          marks: certificateData.marks,
          program: certificateData.program,
          batch: certificateData.batch,
          authentication_result: certificateData.authentication_result
        }])
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Certificate saved successfully:', data.id);
      return data;
    } catch (error) {
      console.error('❌ Error saving certificate:', error);
      throw error;
    }
  }

  /**
   * Find certificate by hash
   */
  async findCertificateByHash(fileHash) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('certificates')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .eq('file_hash', fileHash)
        .single();
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }
      
      if (data) {
        console.log('✅ Certificate found in database:', data.id);
        return {
          ...data,
          institution_name: data.institutions?.name || data.institution
        };
      }
      
      console.log('📝 Certificate not found in database');
      return null;
    } catch (error) {
      console.error('❌ Error finding certificate:', error);
      return null;
    }
  }

  /**
   * Update certificate with authentication result
   */
  async updateCertificateAuthentication(certificateId, authenticationResult) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('certificates')
        .update({ 
          authentication_result: authenticationResult
        })
        .eq('id', certificateId)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('✅ Certificate authentication updated:', certificateId);
      return data;
    } catch (error) {
      console.error('❌ Error updating certificate authentication:', error);
      throw error;
    }
  }

  /**
   * Get recent certificates
   */
  async getRecentCertificates(limit = 10) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('certificates')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .order('upload_date', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      
      console.log(`✅ Retrieved ${data?.length || 0} recent certificates`);
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching recent certificates:', error);
      return [];
    }
  }

  /**
   * Get certificates by institution
   */
  async getCertificatesByInstitution(institutionId) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('certificates')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .eq('institution_id', institutionId)
        .order('upload_date', { ascending: false });
      
      if (error) throw error;
      
      console.log(`✅ Retrieved ${data?.length || 0} certificates for institution:`, institutionId);
      return data || [];
    } catch (error) {
      console.error('❌ Error fetching certificates by institution:', error);
      return [];
    }
  }

  /**
   * Search certificates
   */
  async searchCertificates(query) {
    if (!this.client) throw new Error('Supabase not configured');
    
    try {
      const { data, error } = await this.client
        .from('certificates')
        .select(`
          *,
          institutions (
            id,
            name
          )
        `)
        .or(`student_name.ilike.%${query}%,roll_number.ilike.%${query}%,certificate_type.ilike.%${query}%,institution.ilike.%${query}%`)
        .order('upload_date', { ascending: false });
      
      if (error) throw error;
      
      console.log(`✅ Search found ${data?.length || 0} certificates for query:`, query);
      return data || [];
    } catch (error) {
      console.error('❌ Error searching certificates:', error);
      return [];
    }
  }

  /**
   * Test database connection
   */
  async testConnection() {
    if (!this.client) {
      console.warn('⚠️ Supabase client not configured');
      return false;
    }
    
    try {
      // Try to query institutions table first
      const { error: instError } = await this.client
        .from('institutions')
        .select('count')
        .limit(1);
      
      if (!instError) {
        console.log('✅ Supabase connection successful (institutions table)');
        return true;
      }
      
      // If institutions fails, try certificates table
      const { error: certError } = await this.client
        .from('certificates')
        .select('count')
        .limit(1);
      
      if (!certError) {
        console.log('✅ Supabase connection successful (certificates table)');
        return true;
      }
      
      // Both failed
      throw certError || instError;
      
    } catch (error) {
      console.error('❌ Supabase connection failed:', error.message);
      console.error('Details:', {
        code: error.code,
        hint: error.hint
      });
      return false;
    }
  }
}

// Export singleton instance
export const supabaseClient = new SupabaseClient();

export default supabase;


